// ⚠️ FB-AUTHORIZED SYNTHETIC DEMO CLINICAL STORE (Slack Jun 18) — THU-346 (Slice 2). The Info column's
// editable clinical facts (conditions / allergies / medications), persisted per patient to the demo
// clinical store via the local clinical-facts client. Synthetic data only; NOT the production system of
// record; see DECISIONS.md (D5.1).
//
// ⚠️ This file legitimately carries clinical terms (the Conditions / Allergies / Medications section
// labels) and is therefore explicitly allowlisted in scripts/check-clinical-terms.mjs. The rest of the
// app stays gated — do not copy these strings elsewhere without the same scoped carve-out.
import { useState } from "react";
import { useClinicalFactsApi, type ClinicalFact, type FactKind } from "../lib/clinicalFactsApi";
import { useAsync } from "../lib/useAsync";

const SECTIONS: Array<{ kind: FactKind; label: string; tone: string; placeholder: string }> = [
  { kind: "condition", label: "Conditions", tone: "amber", placeholder: "Add a condition…" },
  { kind: "allergy", label: "Allergies", tone: "red", placeholder: "Add an allergy…" },
  { kind: "medication", label: "Medications", tone: "blue", placeholder: "Add a medication…" },
];

const PlusIcon = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round">
    <line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" />
  </svg>
);
const XIcon = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round">
    <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

export function ClinicalFacts({ patientId }: { patientId: string }) {
  const api = useClinicalFactsApi();
  const { data, loading, error } = useAsync(() => api.list(patientId), [patientId]);
  const [facts, setFacts] = useState<ClinicalFact[] | null>(null);
  const list = facts ?? data ?? [];

  // Seed local state from the fetched list once it arrives, then mutate locally on add/remove.
  if (facts === null && data) setFacts(data);

  async function add(kind: FactKind, name: string) {
    const trimmed = name.trim();
    if (!trimmed) return;
    const created = await api.create(patientId, { kind, name: trimmed });
    setFacts((prev) => [...(prev ?? data ?? []), created]);
  }
  async function remove(id: string) {
    setFacts((prev) => (prev ?? data ?? []).filter((f) => f.id !== id));
    try {
      await api.remove(patientId, id);
    } catch {
      // Re-fetch-free recovery: on failure, drop the optimistic state so the next render reloads.
      setFacts(null);
    }
  }

  if (error) {
    return (
      <div className="pp-info-block">
        <h4>Clinical facts</h4>
        <span className="pp-info-text muted">Couldn&rsquo;t load right now.</span>
      </div>
    );
  }

  return (
    <>
      {SECTIONS.map((s) => {
        const items = list.filter((f) => f.kind === s.kind);
        return (
          <FactSection
            key={s.kind}
            label={s.label}
            tone={s.tone}
            placeholder={s.placeholder}
            items={items}
            loading={loading && facts === null}
            onAdd={(name) => add(s.kind, name)}
            onRemove={remove}
          />
        );
      })}
    </>
  );
}

function FactSection({
  label,
  tone,
  placeholder,
  items,
  loading,
  onAdd,
  onRemove,
}: {
  label: string;
  tone: string;
  placeholder: string;
  items: ClinicalFact[];
  loading: boolean;
  onAdd: (name: string) => Promise<void>;
  onRemove: (id: string) => Promise<void>;
}) {
  const [value, setValue] = useState("");
  const [busy, setBusy] = useState(false);

  async function submit() {
    if (!value.trim() || busy) return;
    setBusy(true);
    try {
      await onAdd(value);
      setValue("");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="pp-info-block">
      <h4>
        {label} <span className="pp-count">({items.length})</span>
      </h4>
      <div className="pp-facts">
        {loading ? (
          <span className="pp-info-text muted">Loading…</span>
        ) : items.length ? (
          <div className="pp-facts-list">
            {items.map((f) => (
              <span className={`pp-fact tag ${tone}`} key={f.id} title={f.notes ?? undefined}>
                <span className="pp-fact-name">{f.name}</span>
                <button
                  type="button"
                  className="pp-fact-rm"
                  aria-label={`Remove ${f.name}`}
                  onClick={() => void onRemove(f.id)}
                >
                  {XIcon}
                </button>
              </span>
            ))}
          </div>
        ) : (
          <span className="pp-info-text muted">None on record</span>
        )}
        <form
          className="pp-fact-add"
          onSubmit={(e) => {
            e.preventDefault();
            void submit();
          }}
        >
          <input
            value={value}
            onChange={(e) => setValue(e.target.value)}
            placeholder={placeholder}
            maxLength={500}
          />
          <button type="submit" disabled={!value.trim() || busy} aria-label={`Add to ${label}`}>
            {PlusIcon}
          </button>
        </form>
      </div>
    </div>
  );
}
